import type { ExtendedUserInfo } from "@services/auth";
import { useAuthentication } from "@services/auth";
import { PermissionsApp, useAuthorization } from "@services/auth/authorization";
import { tieneAccesoOcasSelf } from "@/components/oca/shared/oca-auth";

/**
 * Visibilidad de menús GMAO/prestador:
 * - Usuario PDS (`GESTION_AVANZADA_ACTUACIONES_SELF`): filtra por `ModulosPds` del Profile (tipos de prestador en BBDD).
 * - Resto de usuarios: solo claims Keycloak (comportamiento histórico del portal).
 *
 * Claims como `GESTION_OCAS_SELF` o `EDIT_SELF_WO` no bastan para PDS: mandan los módulos del PDS.
 */

/**
 * Valores de módulo en Profile (ModulosPds):
 * - tipo_prestador.tiene_permiso_modulo
 * - GESTION_OCA_BASICA: PDS (sin tipo OCA) como id_pds en OCA activa
 */
export enum ModulosPdsApp {
    ActuacionesWos = "ACTUACIONES_WOS",
    B2c = "B2C",
    GestionOca = "GESTION_OCA",
    GestionOcaBasica = "GESTION_OCA_BASICA",
    InspeccionesVisuales = "INSPECCIONES_VISUALES",
}

export function tieneModuloPds(userInfo: ExtendedUserInfo | null | undefined, modulo: ModulosPdsApp | string): boolean {
    return userInfo?.tieneModuloPds(modulo) === true;
}

function resolveUserInfo(userInfo?: ExtendedUserInfo | null): ExtendedUserInfo | null {
    return userInfo ?? (useAuthentication().userInfo as ExtendedUserInfo | null);
}

/** Usuario prestador PDS: la visibilidad de menús depende además de ModulosPds. */
export function esUsuarioPds(autho: ReturnType<typeof useAuthorization>): boolean {
    return autho.hasPermission(PermissionsApp.GestionAvanzadaActuacionesSelf);
}

export function usaVistaTecnicoActuaciones(autho: ReturnType<typeof useAuthorization>): boolean {
    return autho.hasPermission(PermissionsApp.GestionBasicaActuacionesSelf)
        || (autho.hasPermission(PermissionsApp.GestionBasicaInspeccionesVisualesSelf)
            && !autho.hasPermission(PermissionsApp.GestionAvanzadaActuacionesAll)
            && !autho.hasPermission(PermissionsApp.GestionAvanzadaActuacionesSelf));
}

export function tieneModuloActuacionesWos(userInfo?: ExtendedUserInfo | null): boolean {
    return tieneModuloPds(resolveUserInfo(userInfo), ModulosPdsApp.ActuacionesWos);
}

/** Prestador OCA (tipo OCA): acciones de gestión de visitas / programación. */
export function tieneModuloGestionOca(userInfo?: ExtendedUserInfo | null): boolean {
    return tieneModuloPds(resolveUserInfo(userInfo), ModulosPdsApp.GestionOca);
}

/** PDS asociado como id_pds en OCA: menú/lectura, sin acciones de prestador OCA. */
export function tieneModuloGestionOcaBasica(userInfo?: ExtendedUserInfo | null): boolean {
    return tieneModuloPds(resolveUserInfo(userInfo), ModulosPdsApp.GestionOcaBasica);
}

/** Menú/rutas listado-detalle OCA para PDS: GESTION_OCA o GESTION_OCA_BASICA. */
export function tieneAccesoMenuOcaPds(userInfo?: ExtendedUserInfo | null): boolean {
    const info = resolveUserInfo(userInfo);
    return tieneModuloGestionOca(info) || tieneModuloGestionOcaBasica(info);
}

export function tieneModuloInspeccionesVisuales(userInfo?: ExtendedUserInfo | null): boolean {
    return tieneModuloPds(resolveUserInfo(userInfo), ModulosPdsApp.InspeccionesVisuales);
}

/** Cluster actuaciones/WO/maestro: PDS → ACTUACIONES_WOS; resto → claims indicados. */
function puedeVerNavActuacionesWos(
    autho: ReturnType<typeof useAuthorization>,
    userInfo: ExtendedUserInfo | null | undefined,
    tieneClaimsNoPds: () => boolean,
): boolean {
    if (esUsuarioPds(autho)) {
        return tieneModuloActuacionesWos(userInfo);
    }

    return tieneClaimsNoPds();
}

export function puedeVerNavDashboardWo(
    autho: ReturnType<typeof useAuthorization>,
    userInfo?: ExtendedUserInfo | null,
): boolean {
    return puedeVerNavActuacionesWos(autho, userInfo, () =>
        autho.hasPermission(PermissionsApp.EditAllWo)
        || autho.hasPermission(PermissionsApp.EditSelfWo));
}

export function puedeVerNavSeguimientoOcas(
    autho: ReturnType<typeof useAuthorization>,
    userInfo?: ExtendedUserInfo | null,
): boolean {
    return autho.hasPermission(PermissionsApp.GestionAvanzadaOcasAll);
}

export function puedeVerNavDashboardKpiGmao(
    autho: ReturnType<typeof useAuthorization>,
): boolean {
    return autho.hasPermission(PermissionsApp.GestionAvanzadaActuacionesAll);
}

export function puedeVerCampanas(
    autho: ReturnType<typeof useAuthorization>,
    userInfo?: ExtendedUserInfo | null,
): boolean {
  return autho.hasPermission(PermissionsApp.GestionCampanas)
}

export function puedeVerNavActuaciones(
    autho: ReturnType<typeof useAuthorization>,
    userInfo?: ExtendedUserInfo | null,
): boolean {
    return puedeVerNavActuacionesWos(autho, userInfo, () =>
        autho.hasPermission(PermissionsApp.GestionAvanzadaActuacionesAll)
        || autho.hasPermission(PermissionsApp.GestionBasicaActuacionesSelf)
        || autho.hasPermission(PermissionsApp.GestionBasicaInspeccionesVisualesSelf));
}

export function puedeVerNavMaestroPagos(
    autho: ReturnType<typeof useAuthorization>,
    userInfo?: ExtendedUserInfo | null,
): boolean {
    return puedeVerNavActuacionesWos(autho, userInfo, () =>
        autho.hasPermission(PermissionsApp.EditAllWo)
        || autho.hasPermission(PermissionsApp.EditSelfWo));
}

export function puedeVerNavMaestroPagosInspeccionesVisuales(
    autho: ReturnType<typeof useAuthorization>,
    userInfo?: ExtendedUserInfo | null,
): boolean {
    if (autho.hasPermission(PermissionsApp.GestionAvanzadaInspeccionesVisualesAll)) {
        return true;
    }

    return autho.hasPermission(PermissionsApp.GestionAvanzadaInspeccionesVisualesSelf)
        && tieneModuloInspeccionesVisuales(userInfo);
}

/** Maestro B2C: admin GMAO ve todo; prestador solo si su PDS tiene tipo B2C. */
export function puedeVerNavMaestroPagosB2c(
    autho: ReturnType<typeof useAuthorization>,
    userInfo?: ExtendedUserInfo | null,
): boolean {
    if (autho.hasPermission(PermissionsApp.GestionAvanzadaActuacionesAll)
        || autho.hasPermission(PermissionsApp.EditAllWo)) {
        return true;
    }

    if (esUsuarioPds(autho)) {
        return tieneModuloPds(resolveUserInfo(userInfo), ModulosPdsApp.B2c);
    }

    return false;
}

/** Menú/ruta «Añadir actuación»: solo gestores GMAO (`GESTION_AVANZADA_ACTUACIONES_ALL`), nunca prestador SELF. */
export function puedeVerNavCrearActuacion(
    autho: ReturnType<typeof useAuthorization>,
    _userInfo?: ExtendedUserInfo | null,
): boolean {
    return autho.hasPermission(PermissionsApp.GestionAvanzadaActuacionesAll);
}

export function puedeVerNavTicketing(
    autho: ReturnType<typeof useAuthorization>,
    userInfo?: ExtendedUserInfo | null,
): boolean {
    return puedeVerNavActuacionesWos(autho, userInfo, () =>
        autho.hasPermission(PermissionsApp.GestionTicketingAll)
        || autho.hasPermission(PermissionsApp.GestionAvanzadaActuacionesAll)
        || autho.hasPermission(PermissionsApp.GestionAvanzadaActuacionesSelf));
}

export function puedeVerNavEquipoMedida(
    autho: ReturnType<typeof useAuthorization>,
    userInfo?: ExtendedUserInfo | null,
): boolean {
    return puedeVerNavActuacionesWos(autho, userInfo, () =>
        autho.hasPermission(PermissionsApp.ReadAllEquipoMedida)
        || autho.hasPermission(PermissionsApp.EditSelfEquipoMedida)
        || autho.hasPermission(PermissionsApp.ReadSelfEquipoMedida));
}

export function puedeVerNavBuscadorPrestadores(
    autho: ReturnType<typeof useAuthorization>,
    userInfo?: ExtendedUserInfo | null,
): boolean {
    return autho.hasPermission(PermissionsApp.EditPrestadores);
}

/** Acceso a detalle/informe de IV (incluye técnico cuya IV está integrada en Actuaciones). */
export function puedeAccederRutasInspeccionesVisuales(
    autho: ReturnType<typeof useAuthorization>,
    userInfo?: ExtendedUserInfo | null,
): boolean {
    if (autho.hasPermission(PermissionsApp.GestionAvanzadaInspeccionesVisualesAll)) {
        return true;
    }

    if (autho.hasPermission(PermissionsApp.GestionAvanzadaInspeccionesVisualesSelf)) {
        return tieneModuloInspeccionesVisuales(userInfo) || esUsuarioPds(autho);
    }

    if (autho.hasPermission(PermissionsApp.GestionBasicaInspeccionesVisualesSelf)) {
        return tieneModuloInspeccionesVisuales(userInfo) || esUsuarioPds(autho);
    }

    if (!esUsuarioPds(autho)) {
        return false;
    }

    return tieneModuloInspeccionesVisuales(userInfo);
}

/** Menú y grid de IV: oculto para técnico con actuaciones básicas (IV integradas en Actuaciones). */
export function puedeVerNavInspeccionesVisuales(
    autho: ReturnType<typeof useAuthorization>,
    userInfo?: ExtendedUserInfo | null,
): boolean {
    if (autho.hasPermission(PermissionsApp.GestionBasicaActuacionesSelf)) {
        return false;
    }

    return puedeAccederRutasInspeccionesVisuales(autho, userInfo);
}

/** Menú/rutas OCA: PDS filtra por módulo BBDD (completa o básica); el resto por claims Keycloak. */
export function puedeVerNavOcas(
    autho: ReturnType<typeof useAuthorization>,
    userInfo?: ExtendedUserInfo | null,
): boolean {
    if (autho.hasPermission(PermissionsApp.GestionAvanzadaOcasAll)) {
        return true;
    }

    if (esUsuarioPds(autho)) {
        return tieneAccesoMenuOcaPds(userInfo);
    }

    return tieneAccesoOcasSelf(autho);
}

/** Ruta de inicio para PDS según ModulosPds (prioridad: actuaciones → OCA → inspecciones). */
export function resolveRutaInicioPds(userInfo?: ExtendedUserInfo | null): string | null {
    if (tieneModuloActuacionesWos(userInfo)) {
        return "/dashboard-wo";
    }

    if (tieneAccesoMenuOcaPds(userInfo)) {
        return "/oca/ocas";
    }

    if (tieneModuloInspeccionesVisuales(userInfo)) {
        return "/inspecciones-visuales";
    }

    return null;
}
